import React, { useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Row, Col, Progress, Tag, Button, Spin, message, Divider, Tabs, Descriptions } from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
  EnvironmentOutlined,
  AlertOutlined
} from "@ant-design/icons";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import dayjs from "dayjs";
import useProject from "../../../hooks/projects/useProject";
import useProjectTracking from "../../../hooks/projects/useProjectTracking";
import "./SchemeDetail.css";

const formatDate = (date) => (date ? dayjs(date).format("DD MMM YYYY") : "—");

const getStatusInfo = (status) => {
  if (!status) return { label: "Not Started", color: "#6b7280", icon: <ClockCircleOutlined /> };

  const s = String(status).toUpperCase().trim();

  if (s === "YES") return { label: "Completed", color: "#1a9e5c", icon: <CheckCircleOutlined /> };
  if (s === "NO") return { label: "No", color: "#e07b1a", icon: <ClockCircleOutlined /> };
  if (s === "NA") return { label: "Not Applicable", color: "#9aa2ac", icon: <CloseCircleOutlined /> };
  if (s === "NOT_STARTED") return { label: "Not Started", color: "#6b7280", icon: <ClockCircleOutlined /> };

  return { label: s.replace(/_/g, " "), color: "#2e6fdb", icon: <ClockCircleOutlined /> };
};

const cleanTitle = (str) => {
  return str
    .replace(/_/g, " ")
    .replace(/status|date_planned|date_actual|remarks|planned|actual|name/gi, "")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

function SchemeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { project, loading: projectLoading, fetchProject } = useProject();
  const { tracking, loading: trackingLoading, fetchTracking } = useProjectTracking();

  const hasFetched = useRef(false);

  const overallProgress = tracking?.total_project_progress ?? project?.overall_progress ?? 0;

  const pieData = useMemo(() => [
    { name: "Completed", value: overallProgress, color: "#1a9e5c" },
    { name: "Remaining", value: Math.max(0, 100 - overallProgress), color: "#e5e7eb" },
  ], [overallProgress]);

  // Dynamic sections from section_progress_breakdown
  const dynamicSections = useMemo(() => {
    if (!project?.section_progress_breakdown) return [1, 2, 3, 4, 5];

    const keys = Object.keys(project.section_progress_breakdown);
    return keys
      .map(k => parseInt(k.replace(/[^0-9]/g, '')))
      .filter(Boolean)
      .sort((a, b) => a - b);
  }, [project?.section_progress_breakdown]);

  useEffect(() => {
    if (!id || hasFetched.current) return;

    const loadData = async () => {
      try {
        hasFetched.current = true;
        await Promise.all([fetchProject(id), fetchTracking(id)]);
      } catch (error) {
        console.error("Failed to load project data:", error);
        message.error("Failed to load project details");
      }
    };

    loadData();
  }, [id, fetchProject, fetchTracking]);

  const loading = projectLoading || trackingLoading;

  if (loading && !project) {
    return <div className="scheme-detail-loading"><Spin size="large" /><p>Loading project details...</p></div>;
  }

  if (!project) return <div className="scheme-detail-loading">Project not found</div>;

  const landTypeBadge = {
    GOVT: { label: "Government Land", color: "#0ea5e9" },
    MIDC: { label: "MIDC Land", color: "#8b5cf6" },
    PRIVATE: { label: "Private Land", color: "#f59e0b" },
  }[project?.land_type] ?? { label: project?.land_type || "Unknown", color: "#6b7280" };

  return (
    <div className="scheme-detail-container">
      {/* Header */}
      <Card className="scheme-detail-header" bordered={false}>
        <div className="header-left-side">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/schemes")} shape="circle" />
          <div className="header-content">
            <div className="header-top">
              <h1>{project.name}</h1>
              <Tag className="land-type-badge" style={{
                background: landTypeBadge.color + "15",
                color: landTypeBadge.color,
                borderColor: landTypeBadge.color + "30",
              }}>
                {landTypeBadge.label}
              </Tag>
              {project.total_project_delay_days > 0 && (
                <Tag color="error" icon={<AlertOutlined />}>
                  Delayed by {project.total_project_delay_days} Days
                </Tag>
              )}
            </div>
            <p className="location">
              <EnvironmentOutlined /> {[
                project.district_name || project.district,
                project.taluka_name || project.taluka,
                project.village
              ].filter(Boolean).join(" • ")}
              <span className="geo-coords"> ({project.latitude}, {project.longitude})</span>
            </p>
          </div>
        </div>

        <div className="header-progress">
          <div style={{ width: 75, height: 75 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={pieData} dataKey="value" innerRadius={26} outerRadius={36} startAngle={90} endAngle={-270}>
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="progress-text">
            <span className="progress-value">{Math.round(overallProgress)}%</span>
            <span className="progress-label">Overall Progress</span>
          </div>
        </div>
      </Card>

      {/* Meta + Linear Progress */}
      <Row gutter={[20, 20]} className="main-content-row">
        <Col xs={24} xl={10}>
          <Card title="Project Specification & Personnel" className="info-card">
            <Descriptions column={2} layout="vertical" size="small">
              <Descriptions.Item label="Zone">{project.zone_name || "—"}</Descriptions.Item>
              <Descriptions.Item label="Circle / Division">
                {project.circle_name || "—"} / {project.div_name || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="STU Plan Year">{project.stu_year || "—"}</Descriptions.Item>
              <Descriptions.Item label="Voltage / Tech">
                {project.voltage} kV / {project.technology}
              </Descriptions.Item>
              <Descriptions.Item label="Land Required">
                {project.land_area_required || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Start Date">{formatDate(project.assignment_start_date)}</Descriptions.Item>
              <Descriptions.Item label="Assigned Engineer" span={2}>
                <div className="personnel-chip">
                  <UserOutlined /> <strong>{project.assigned_engineer_name || "—"}</strong>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Subdivision Officer (SDO)" span={2}>
                <div className="personnel-info">
                  <strong>{project.subdivision_officer_name || "—"}</strong>
                  <div>{project.sdo_contact} • {project.sdo_email}</div>
                </div>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} xl={14}>
          <Card title="Linear Workflow Progress" className="progress-card">
            <div className="sections-row">
              {dynamicSections.map((num) => {
                const progressKey = Object.keys(project.section_progress_breakdown || {})
                  .find(k => k.includes(num.toString()));

                const progress = project.section_progress_breakdown?.[progressKey] ?? 0;
                const targetDate = tracking?.[`section${num}_planned_date`];

                return (
                  <div className="section-item" key={num}>
                    <Progress type="circle" percent={Math.round(progress)} size={64} strokeColor="#1a9e5c" />
                    <div className="section-label">
                      <span>Section {num}</span>
                      <small className="target-date">Target: {formatDate(targetDate)}</small>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Granular Phase Breakdown */}
      {tracking && (
        <Card title="Granular Phase Breakdown" className="tracking-detail-card" style={{ marginTop: 20 }}>
          <Tabs
            type="card"
            items={dynamicSections.map((secNum) => {
              // Find correct section key (handles sec1, m_sec1, p_sec1 etc.)
              const sectionKey = `section${secNum}`;
              const sectionData = tracking[sectionKey];

              if (!Array.isArray(sectionData) || sectionData.length === 0) return null;

              return {
                label: `Section ${secNum}`,
                key: String(secNum),
                children: (
                  <div className="tasks-list-container">
                    {sectionData.map((item, idx) => {
                      const entries = Object.entries(item);
                      const statusEntry = entries.find(([k]) => k.endsWith("_status"));
                      const datePlanned = entries.find(([k]) => k.endsWith("_date_planned"));
                      const dateActual = entries.find(([k]) => k.endsWith("_date_actual"));
                      const remarksEntry = entries.find(([k]) => k.endsWith("_remarks"));

                      const rawKey = Object.keys(item)[0] || "";
                      let displayTitle = cleanTitle(rawKey);

                      // === Special Handling ===

                      // Area + Distance
                      if (rawKey.includes("area_of_feasible_land") || rawKey.includes("distance_from_load_centre")) {
                        const area = item["area_of_feasible_land_(ha/r)_area"] || item["area_of_feasible_land_(ha/r)_area"] || "—";
                        const distance = item["distance_from_load_centre_(km)_distance"] || "—";
                        displayTitle = "Area & Distance from Load Centre";

                        return (
                          <div key={idx} className="task-item-row">
                            <Row gutter={[16, 8]} align="middle">
                              <Col xs={24} md={10}><div className="task-title-main"><span className="task-index">{idx + 1}.</span> {displayTitle}</div></Col>
                              <Col xs={12} md={6}><div style={{ color: "#555", fontSize: "14px" }}>{area} Ha • {distance} km</div></Col>
                              <Col xs={12} md={8}>
                                {datePlanned && <div><strong>Target:</strong> {formatDate(datePlanned[1])}</div>}
                                {dateActual && <div><strong>Actual:</strong> {formatDate(dateActual[1])}</div>}
                              </Col>
                            </Row>
                            {remarksEntry?.[1] && <div className="task-remarks-bubble">{remarksEntry[1]}</div>}
                            {idx < sectionData.length - 1 && <Divider className="minimal-divider" />}
                          </div>
                        );
                      }

                      // Distance of Most Feasible Land from Load Centre (Private Land form: value + remarks only, no status field)
                      if (rawKey.includes("distance_of_most_feasible_land")) {
                        const distance = item[rawKey] || "—";
                        displayTitle = "Distance Of Most Feasible Land From Load Centre";

                        return (
                          <div key={idx} className="task-item-row">
                            <Row gutter={[16, 8]} align="middle">
                              <Col xs={24} md={10}><div className="task-title-main"><span className="task-index">{idx + 1}.</span> {displayTitle}</div></Col>
                              <Col xs={12} md={6}><div style={{ color: "#555", fontSize: "14px" }}>{distance} km</div></Col>
                              <Col xs={12} md={8}>
                                {datePlanned && <div><strong>Target:</strong> {formatDate(datePlanned[1])}</div>}
                                {dateActual && <div><strong>Actual:</strong> {formatDate(dateActual[1])}</div>}
                              </Col>
                            </Row>
                            {remarksEntry?.[1] && <div className="task-remarks-bubble">{remarksEntry[1]}</div>}
                            {idx < sectionData.length - 1 && <Divider className="minimal-divider" />}
                          </div>
                        );
                      }

                      // Feasible Land Details
                      if (rawKey.includes("mouje_details") || item.title?.toLowerCase().includes("feasible")) {
                        displayTitle = "Details of Feasible Land";
                        return (
                          <div key={idx} className="task-item-row">
                            <Row gutter={[16, 8]}>
                              <Col xs={24} md={10}><div className="task-title-main"><span className="task-index">{idx + 1}.</span> {displayTitle}</div></Col>
                              <Col xs={24} md={14}>
                                <div style={{ color: "#555", lineHeight: 1.6 }}>
                                  <strong>Mouje:</strong> {item.mouje_details || "—"}<br />
                                  <strong>Survey/Gut No:</strong> {item["surveyno/gutno_details"] || "—"}
                                </div>
                              </Col>
                            </Row>
                            {idx < sectionData.length - 1 && <Divider className="minimal-divider" />}
                          </div>
                        );
                      }

                      const statusValue = statusEntry?.[1];

                      // Hide optional "Any Other NOC" if not used
                      if (rawKey.startsWith("any_other_noc_") && statusValue !== "YES") {
                        return null;
                      }

                      const statusInfo = getStatusInfo(statusValue);

                      return (
                        <div key={idx} className="task-item-row">
                          <Row gutter={[16, 8]} align="middle">
                            <Col xs={24} md={10}>
                              <div className="task-title-main">
                                <span className="task-index">{idx + 1}.</span> {displayTitle}
                              </div>
                            </Col>
                            <Col xs={12} md={5}>
                              <Tag color={statusInfo.color} className="status-tag-pill">
                                {statusInfo.icon} {statusInfo.label}
                              </Tag>
                            </Col>
                            <Col xs={12} md={9}>
                              <div className="task-timeline-dates">
                                {datePlanned && <div><strong>Target:</strong> {formatDate(datePlanned[1])}</div>}
                                {dateActual && <div><strong>Actual:</strong> {formatDate(dateActual[1])}</div>}
                              </div>
                            </Col>
                          </Row>
                          {remarksEntry?.[1] && remarksEntry[1] !== "NA" && (
                            <div className="task-remarks-bubble">{remarksEntry[1]}</div>
                          )}
                          {idx < sectionData.length - 1 && <Divider className="minimal-divider" />}
                        </div>
                      );
                    })}
                  </div>
                )
              };
            }).filter(Boolean)}
          />
        </Card>
      )}
    </div>
  );
}

export default SchemeDetail;